import { useEffect, useState } from 'react';
import { CheckIcon } from '@radix-ui/react-icons';
import type { DocumentRenderAttachmentSummary } from '@vivport/backend/types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ObservationCapture } from '@/components/widget/observation-capture/observation-capture';
import { useCaptureFlowStore } from '@/store/useCaptureFlowStore';

interface TemplateEntryPhotoPickerProps {
  sectionId: number;
  photos: DocumentRenderAttachmentSummary[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
  idPrefix: string;
  capture?: boolean;
}

export function TemplateEntryPhotoPicker({
  sectionId,
  photos,
  selectedIds,
  onChange,
  disabled,
  idPrefix,
  capture = true,
}: TemplateEntryPhotoPickerProps) {
  const [capturing, setCapturing] = useState(false);
  const [sessionId] = useState(() => `entry-photos-${crypto.randomUUID()}`);
  const endSession = useCaptureFlowStore((state) => state.endSession);

  useEffect(() => () => endSession(sessionId), [endSession, sessionId]);

  const toggle = (id: number, checked: boolean) => {
    onChange(checked ? [...new Set([...selectedIds, id])] : selectedIds.filter((entry) => entry !== id));
  };

  // A photo whose bytes are already on the server but which the render has not listed yet — the
  // upload landed and the entry save has not. It stays selected and is never re-uploaded.
  const items = [
    ...photos.map((photo) => ({
      id: photo.id,
      label: photo.title.length > 0 ? photo.title : photo.code,
      unlisted: false,
    })),
    ...selectedIds
      .filter((id) => !photos.some((photo) => photo.id === id))
      .map((id) => ({ id, label: 'Uploaded · not yet attached', unlisted: true })),
  ];

  return (
    <div className="space-y-2">
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">No photos attached yet.</p>
      ) : (
        <>
          <ul className="max-h-40 space-y-1 overflow-y-auto rounded-md border p-2 max-sm:hidden">
            {items.map((item) => {
              const controlId = `${idPrefix}-photo-${item.id}`;
              return (
                <li key={item.id} className="flex items-center gap-2">
                  <Checkbox
                    id={controlId}
                    checked={selectedIds.includes(item.id)}
                    onCheckedChange={(checked) => toggle(item.id, checked === true)}
                    disabled={disabled}
                  />
                  <Label
                    htmlFor={controlId}
                    className={`text-sm font-normal ${item.unlisted ? 'italic text-muted-foreground' : ''}`}
                  >
                    {item.label}
                  </Label>
                </li>
              );
            })}
          </ul>
          <ul className="hidden max-sm:grid max-sm:max-h-64 max-sm:grid-cols-3 max-sm:gap-2 max-sm:overflow-y-auto">
            {items.map((item) => {
              const selected = selectedIds.includes(item.id);
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    aria-pressed={selected}
                    disabled={disabled}
                    onClick={() => toggle(item.id, !selected)}
                    className={`relative flex min-h-24 w-full flex-col justify-end rounded-md border-2 p-1.5 text-left text-[11px] leading-tight disabled:opacity-50 ${
                      selected ? 'border-primary bg-primary/10 font-medium' : 'border-input bg-muted/40'
                    }`}
                  >
                    {selected ? (
                      <span className="absolute right-1 top-1 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <CheckIcon className="size-3.5" />
                      </span>
                    ) : null}
                    <span className={`line-clamp-2 break-words ${item.unlisted ? 'italic' : ''}`}>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
      {capture ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="max-sm:h-12 max-sm:w-full max-sm:text-base"
          onClick={() => setCapturing((value) => !value)}
          disabled={disabled}
        >
          {capturing ? 'Hide capture' : 'Capture'}
        </Button>
      ) : null}
      {capture && capturing ? (
        <ObservationCapture
          sessionId={sessionId}
          defaultMode="photo"
          linkContentId={sectionId}
          onSubmitted={({ photoIds }) => {
            if (photoIds.length > 0) onChange([...new Set([...selectedIds, ...photoIds])]);
          }}
        />
      ) : null}
    </div>
  );
}
